import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import { terminalColors } from '../../theme/terminalStyles';
import { useDesktopModalInput } from '../../hooks/useDesktopModalInput';
import {
  evaluateAddExpression,
  formatQty,
  sanitizeAddExpression,
} from '../../helpers/addExpression';

const dialogPaperSx = {
  bgcolor: terminalColors.gradientTop,
  backgroundImage: 'none',
  borderRadius: 2,
  m: { xs: 1, sm: 2 },
};

const CALC_LAYOUT = {
  default: ['1 2 3 *', '4 5 6 /', '7 8 9 -', '{clearAll} 0 . +', '{bksp} {equals}'],
};

const CALC_DISPLAY = {
  '{bksp}': '⌫',
  '{clearAll}': 'C',
  '{equals}': '=',
  '*': '×',
  '/': '÷',
  '-': '−',
};

const keyboardCss = `
.stocktake-calc-keyboard.stocktake-calc-keyboard .hg-row .hg-button {
  width: 25%;
  max-width: 25%;
  flex-grow: 0;
  height: 48px;
}
.stocktake-calc-keyboard.stocktake-calc-keyboard .hg-row:last-child {
  justify-content: flex-end;
}
.stocktake-calc-keyboard.stocktake-calc-keyboard .hg-button.hg-op {
  background: #f3e6d4;
  font-weight: 700;
}
`;

const StocktakeCalcAddDialog = ({
  open,
  currentQty,
  suggestedAdd,
  onClose,
  onConfirm,
  isSaving,
}) => {
  const [value, setValue] = useState('');
  const keyboardRef = useRef(null);

  const preview = useMemo(() => {
    const evaluated = evaluateAddExpression(value);
    if (!evaluated.ok) return null;
    const current = Number(currentQty ?? 0);
    const base = Number.isFinite(current) ? current : 0;
    const next = Math.max(0, Math.round((base + evaluated.value) * 1e6) / 1e6);
    return { delta: evaluated.value, next };
  }, [value, currentQty]);

  useEffect(() => {
    if (!open) return;
    const initial = formatQty(suggestedAdd ?? 0) || '0';
    setValue(initial);
    keyboardRef.current?.setInput(initial);
    keyboardRef.current?.setCaretPosition?.(initial.length);
  }, [open, suggestedAdd]);

  const syncInput = useCallback((next) => {
    setValue(next);
    const keyboard = keyboardRef.current;
    if (keyboard && keyboard.getInput() !== next) {
      keyboard.setInput(next);
    }
  }, []);

  const applyInput = useCallback(
    (raw) => {
      syncInput(sanitizeAddExpression(raw));
    },
    [syncInput]
  );

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleConfirm = useCallback(() => {
    if (isSaving || !preview) return;
    onConfirm(preview.delta);
  }, [isSaving, onConfirm, preview]);

  const { inputRef, handleInputKeyDown } = useDesktopModalInput({
    open,
    onConfirm: handleConfirm,
    enabled: !isSaving,
    focusKey: suggestedAdd,
    selectOnFocus: false,
  });

  useEffect(() => {
    if (!open) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        const end = el.value.length;
        try {
          el.setSelectionRange(end, end);
        } catch {
          /* ignore */
        }
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open, suggestedAdd, inputRef]);

  const deltaLabel =
    preview == null ? '' : preview.delta < 0 ? formatQty(preview.delta) : `+${formatQty(preview.delta)}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <style>{keyboardCss}</style>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          fontWeight: 600,
          color: terminalColors.textPrimary,
          pb: 0.5,
        }}
      >
        <span>Кількість (факт)</span>
        <Box component="span" sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>
          {formatQty(currentQty ?? 0)}
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          value={value}
          inputRef={inputRef}
          onKeyDown={handleInputKeyDown}
          onChange={(e) => applyInput(e.target.value)}
          margin="dense"
          inputProps={{ inputMode: 'decimal', 'aria-label': 'Додати кількість' }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: terminalColors.cardWhite } }}
        />
        <Box className="stocktake-calc-keyboard">
          <Keyboard
            keyboardRef={(r) => {
              keyboardRef.current = r;
            }}
            onChange={applyInput}
            layout={CALC_LAYOUT}
            display={CALC_DISPLAY}
            theme="hg-theme-default"
            buttonTheme={[
              {
                class: 'hg-op',
                buttons: '* / - + {equals}',
              },
            ]}
            onKeyPress={(button) => {
              const current = keyboardRef.current?.getInput?.() ?? value;
              if (button === '{clearAll}') {
                syncInput('');
                return;
              }
              if (button === '{equals}') {
                const evaluated = evaluateAddExpression(current);
                if (!evaluated.ok) return;
                syncInput(formatQty(evaluated.value));
              }
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            mt: 1.5,
            minHeight: 36,
            px: 0.5,
          }}
        >
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: terminalColors.textPrimary }}>
            {deltaLabel}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: terminalColors.textPrimary }}>
            {preview ? formatQty(preview.next) : ''}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <TerminalOrangeButton
          fullWidth
          onClick={handleClose}
          disabled={isSaving}
          sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
        >
          Відміна
        </TerminalOrangeButton>
        <TerminalOrangeButton
          fullWidth
          size="large"
          onClick={handleConfirm}
          disabled={isSaving || !preview}
          sx={{ minHeight: 64, fontSize: '1.35rem' }}
        >
          {isSaving ? <CircularProgress size={22} color="inherit" /> : 'OK'}
        </TerminalOrangeButton>
      </DialogActions>
    </Dialog>
  );
};

export default StocktakeCalcAddDialog;
